import React, { useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/Modal/Modal';
import Select from '@/components/forms/Select/Select';
import Button from '@/components/forms/Button/Button';
import Alert from '@/components/Alert/Alert';
import styles from './CancelModal.module.css';
import Textarea from '@/components/forms/Textarea/Textarea';
import { Link } from 'wouter';
import { CancellationReasons } from '@boxem/core-api-schema';
import Flex from '@/components/Flex/Flex';

const REASON_LABELS = {
  [CancellationReasons.MISSING_FEATURES]: "The app doesn't have features I need",
  [CancellationReasons.NOT_USING_ENOUGH]: "I am not using Boxem as much as I expected",
  [CancellationReasons.TECHNICAL_ISSUES]: "I had technical issues or bugs",
  [CancellationReasons.DIFFICULT_TO_USE]: "I found Boxem difficult to use",
  [CancellationReasons.WORKFLOW_INCOMPATIBLE]: "It doesn't work well with my workflow",
  [CancellationReasons.TOO_EXPENSIVE]: "It's too expensive for me",
  [CancellationReasons.TEMPORARY_NEED]: "I only needed it temporarily",
  [CancellationReasons.OTHER]: "Other"
};

export default function CancelModal({
  open,
  onOpenChange,
  onCancel,
  onKeep,
  billingEndDate,
  submitCancellationReason,
}) {
  const [selectedReason, setSelectedReason] = useState<CancellationReasons | null>(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 2500;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      await submitCancellationReason(selectedReason, feedback);
      onCancel();
    } catch (err) {
      console.error('Failed to submit cancellation reason:', err);
      setError('Failed to submit your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFeedbackChange = (text: string) => {
    if (text.length <= MAX_CHARS) {
      setFeedback(text);
      setCharCount(text.length);
    }
  };
  
  const reasonOptions = Object.values(REASON_LABELS);
  
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className={styles.modal}
    >
      <form>
        <ModalHeader divider={false} className={styles.modalHeader}>
            <h2 className={styles.title}>Cancel Subscription to Boxem.</h2>
            <p className={styles.subtitle}>We're sorry to see you go! Hope we get to see you again in the future.</p>
        </ModalHeader>
        
        <ModalBody className={styles.modalBody}>
            <Alert 
                type="warning" 
                className={styles.alert}
                message={
                    <p>
                    Your Subscription will be active for the remaining time until your billing period ends on <strong>{billingEndDate}</strong>. After that, you'll lose access to all Boxem features.
                    </p>
                }
            />
            
            <p className={styles.feedbackIntro}>
            Before you go, would you mind letting us know why? Your feedback will make us improve the app for other people.
            </p>
            <Flex direction='column' gap={6} style={{width:"100%"}}>
                <Flex gap={6} align='center' style={{width:"100%"}}>
                    <label htmlFor="cancel-reason" className={styles.label}>Reason:</label>
                    <Select
                        id="cancel-reason"
                        options={Object.keys(REASON_LABELS) as CancellationReasons[]}
                        value={selectedReason}
                        onChange={(value) => setSelectedReason(value)}
                        placeholder="Select Reason"
                        fullWidth
                        optionRender={(option) => REASON_LABELS[option]}
                    />
                </Flex>
                
                <Flex gap={2}>
                <label htmlFor="cancel-feedback" className={styles.label}>Feedback:</label>
                <Flex gap={2} align='start' direction='column' style={{width:"100%"}}>
                    <Textarea
                    maxLength={MAX_CHARS}
                    inputId="cancel-feedback"
                    value={feedback}
                    onChange={handleFeedbackChange}
                    placeholder="Please provide feedback"
                    />
                    <div className={styles.charCount}>
                    {charCount} / {MAX_CHARS} characters remaining
                    </div>
                </Flex>
                </Flex>
            </Flex>
            
            {error && <div className={styles.error}>{error}</div>}
            
            <p className={styles.supportNote}>
            Looking to send us feedback without cancelling? <Link href="~/more" className={styles.supportLink}>Contact support here</Link>
            </p>
        </ModalBody>
        
        <ModalFooter className={styles.footer}>
            <Button 
                type="secondary" 
                onClick={onKeep}
            >
                Keep Subscription
            </Button>
            <Button 
                type="danger" 
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedReason}
            >
                Cancel Subscription
            </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
